<script setup lang="ts">
import { computed, ref, toRefs } from 'vue';
import { PageName } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import RelatedArticle from '@/components/common/adp-document/related-article.vue';
import { AdpDocument } from '@/types';

interface Props {
  relatedArticles: AdpDocument[];
  pageName: PageName;
  relatedQuery?: string;
  requestId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  relatedQuery: '',
  requestId: '',
});

const emit = defineEmits<{
  close: [];
}>();

const { relatedArticles, relatedQuery } = toRefs(props);
const articleContainer = ref(undefined as HTMLDivElement | undefined);
const currentArticleIndex = ref(0);
const adpDocument = computed(
  () => relatedArticles.value?.[currentArticleIndex.value],
);
const showAnotherArticle = () => {
  if (currentArticleIndex.value === 0) {
    currentArticleIndex.value = 1;
    articleContainer.value?.scrollBy({
      left: articleContainer.value.scrollWidth,
    });
  } else {
    currentArticleIndex.value = 0;
    articleContainer.value?.scrollBy({
      left: -articleContainer.value.scrollWidth,
    });
  }
};

const setRelatedQuery = (
  requestId: string,
  relatedQuery: string,
  rank: number,
) => {
  localStorage.setItem(
    'relatedQuery',
    JSON.stringify({
      feature: 'related_contents',
      requestId: requestId,
      searchQuery: relatedQuery,
      rank: rank,
    }),
  );
};

const queryWords = computed(() => relatedQuery.value.split(' '));
</script>

<template>
  <div class="related-articles-display c-text-regular--m">
    <div class="header">
      <div class="related-articles-label c-text-regular--s">
        <DgrIcon name="sparkles-fill" />
        <span>
          <template
            v-for="(query, i) in queryWords"
            :key="`query-${i}-${query}`"
          >
            <span class="c-title-bold--s">「{{ query }}」</span>
            <template v-if="i < queryWords.length - 1">と</template>
          </template>
          に関するニュースを見つけました。
        </span>
      </div>
      <DgrIcon
        size="small"
        :keep-fill="false"
        name="times"
        class="clickable close-icon"
        @click="emit('close')"
      />
    </div>
    <template v-if="adpDocument">
      <div
        :class="relatedArticles.length === 1 ? 'one-article' : 'two-articles'"
        :ref="el => (articleContainer = el as HTMLDivElement)"
      >
        <template v-for="(article, index) in relatedArticles" :key="article.id">
          <RelatedArticle
            :related-article="article"
            :page-name="pageName"
            :rank-in-whole-feed="index + 1"
            :request-id="requestId"
            :related-query="relatedQuery"
          />
        </template>
      </div>
      <div class="query-container">
        <DgrIcon size="xs" name="search" class="primary search-icon" />
        <router-link
          :to="{
            name: 'searchAll',
            query: { query: relatedQuery },
          }"
          target="_blank"
          rel="noopener noreferrer"
          class="related-query"
          @click="
            setRelatedQuery(requestId, relatedQuery, currentArticleIndex + 1)
          "
        >
          <span class="c-title-bold--s">{{ relatedQuery }}で検索する</span>
        </router-link>
      </div>
      <div v-if="relatedArticles.length > 1">
        <button
          @click="showAnotherArticle"
          class="another-article-button"
          :class="{
            right: currentArticleIndex == 0,
            left: currentArticleIndex == 1,
          }"
        >
          <DgrIcon
            size="small"
            name="angle-right"
            v-if="currentArticleIndex == 0"
          />
          <DgrIcon size="small" name="angle-left" v-else />
        </button>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.related-articles-display {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: fit-content;
  background: $color-gray200;
  box-sizing: border-box;
  border-radius: 4px;
  padding: 16px;

  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;

  .icon-box {
    flex-shrink: 0;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    .related-articles-label {
      display: flex;
      align-items: center;
    }

    .close-icon {
      color: $color-gray600;
    }
  }

  .query-container {
    display: flex;
    align-items: center;
    margin-top: 10px;
    z-index: 1;
    .search-icon {
      margin-right: 4px;
    }
    .related-query {
      color: $color-green600;
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  &:hover .another-article-button {
    display: flex;
  }
  .another-article-button {
    display: none;
    align-items: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: #ffffff;
    border: none;
    cursor: pointer;
    border-radius: 50%;
    padding: 0;
    width: 28px;
    height: 28px;
    box-shadow: 0px 1px 5px 0px rgba($color-gray1000, 0.25);

    &.left {
      left: 0px;
    }
    &.right {
      right: 0px;
    }
  }

  .one-article {
    position: relative;
    width: 100%;
    .article-container {
      width: auto;
    }
  }
  .two-articles {
    align-items: center;
    position: relative;
    width: 100%;
    display: flex;
    gap: 4px;
    overflow: hidden;
    padding-bottom: 40px;
    margin-bottom: -40px;
    .article-container {
      width: 90%;
      flex-shrink: 0;
    }
  }
}
</style>
